import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import {
  ChangeProject,
  ClearCurrentCp,
  // ClearCurrentMcp,
  ClearCurrentTask,
  ClearProjectUsers,
  // ClearPunches,
  ClearScopeOfWork,
  CpsLoad,
  HeatTraceConfiguredLoad,
  HeatTraceConfiguredLoadSuccess,
  HeatTraceClearAll,
  LoadFailure,
  ProjectAccessLoad,
  ProjectActionTypes,
  ProjectConfigLoadSuccess,
  ProjectConfigUpdate,
  ProjectDeleteImage,
  ProjectImageUpload,
  ProjectInjectsCheckSheetItemValues,
  ProjectsLoadSuccess,
  ProjectTreeLoad,
  ProjectTreeLoadSuccess,
  TasksLoad,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentProject, getCurrentSite } from '@completion/selectors';
import { ProjectService } from '../../services';
import { Project, Site } from '@completion/models';
import { trackResource } from './resource-fetch.util';
import { NotificationService } from '../../services/notification.service';
import { ProjectsLoadFailure } from '../actions';

@Injectable()
export class ProjectEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly projectService: ProjectService,
    private readonly store: Store<State>,
    private readonly notifications: NotificationService,
  ) {}

  loadProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectActionTypes.ProjectsLoad),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        service: this.projectService,
        method: 'getProjects',
        serviceArgs: () => [],
        onSuccess: (projects: Project[]) => {
          const list = projects as any[] | undefined;
          const count = list?.length ?? 0;
          this.notifications.success(count ? `Loaded ${count} projects.` : 'Projects loaded.', 'Projects fetched');

          // set current project
          const savedProjectId = window.localStorage.getItem('projectId') ? Number(window.localStorage.getItem('projectId')) : null;
          const savedProject = projects.find((p) => p.id === savedProjectId);
          const currentProject = savedProject ? savedProject : projects[0];
          return new ProjectsLoadSuccess(projects, currentProject);
        },
        onError: (error) => {
          this.store.dispatch(new LoadFailure(error));
          return new ProjectsLoadFailure(error);
        },
      }),
    ),
  );

  checkProjectAccess = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectActionTypes.ProjectsLoadSuccess),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      switchMap(([_action, currentProject]) => {
        if (currentProject) {
          return [new ProjectAccessLoad(currentProject)];
        }
        return EMPTY;
      }),
    ),
  );

  loadSidePanelData = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectsLoadSuccess>(ProjectActionTypes.ProjectsLoadSuccess),
      tap((arfs) => console.log(arfs)),
      switchMap((action: ProjectsLoadSuccess) => [
        new TasksLoad(action.currentProject.id),
        new CpsLoad(),
        new ProjectTreeLoad(),
        new HeatTraceConfiguredLoad(action.currentProject.id),
      ]),
    ),
  );

  changeProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ChangeProject>(ProjectActionTypes.ChangeProject),
      switchMap((action) => [
        new TasksLoad(action.project.id),
        new CpsLoad(),
        new ClearCurrentTask(),
        new ClearCurrentCp(),
        // new ClearCurrentMcp(),
        new ClearProjectUsers(),
        new ClearScopeOfWork(),
        // new ClearPunches(),
        new HeatTraceClearAll(),
        new ProjectTreeLoad(),
        new HeatTraceConfiguredLoad(action.project.id),
      ]),
      catchError((err) => of(new LoadFailure(err))),
    ),
  );

  loadHeatTraceConfigured$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HeatTraceConfiguredLoad>(ProjectActionTypes.HeatTraceConfiguredLoad),
      switchMap((action) =>
        this.projectService.getHeatTraceConfigured(action.projectId).pipe(
          switchMap((response) => [new HeatTraceConfiguredLoadSuccess(response.configured)]),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  loadProjectTree$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectTreeLoad>(ProjectActionTypes.ProjectTreeLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentSite) as Observable<Site>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        service: this.projectService,
        method: 'getProjectTree',
        serviceArgs: ([_, currentProject, currentSite]) => [currentProject.id, currentSite?.id],
        onSuccess: (treeNodes) => new ProjectTreeLoadSuccess(treeNodes as any),
      }),
    ),
  );

  uploadProjectImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectImageUpload>(ProjectActionTypes.ProjectImageUpload),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,

        service: this.projectService,
        method: 'uploadProjectImage',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.imageType, action.attachment],
        onSuccess: () => ({ type: '[Project] Image Upload Success' }),
      }),
    ),
  );

  deleteProjectImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectDeleteImage>(ProjectActionTypes.ProjectDeleteImage),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        service: this.projectService,
        method: 'deleteProjectImage',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.imageType],
        onSuccess: () => ({ type: '[Project] Image Delete Success' }),
      }),
    ),
  );

  updateProjectConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectConfigUpdate>(ProjectActionTypes.ProjectConfigUpdate),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        service: this.projectService,
        method: 'updateProjectConfig',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.projectConfig],
        onSuccess: (config) => {
          return new ProjectConfigLoadSuccess(config as any);
        },
      }),
    ),
  );

  injectCsItemValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectInjectsCheckSheetItemValues>(ProjectActionTypes.ProjectInjectsCheckSheetItemValues),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        service: this.projectService,
        method: 'injectCsItemValues',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.data],
        onSuccess: () => ({ type: '[Project] Inject CS Item Values Success' }),
      }),
    ),
  );
}
